import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  BehaviorSubject, Observable, combineLatest, map, Subject, takeUntil
} from 'rxjs';
import { IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisIconButtonModule, IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { IPageInfo, IPaginationViewModel } from '../../models/pagination.interfaces';

const PAGINATION_DEFAULTS = {
  totalItems: 0,
  itemsPerPage: 8,
  currentPage: 1,
  visiblePages: 6,
  maxMiddlePages: 4,
  minItemsPerPage: 1,
  minPageNumber: 1,
  startEdgeThreshold: 4,
  endEdgeThreshold: 3
};

@Component({
  selector: 'sondermind-pagination',
  standalone: true,
  imports: [CommonModule, IrisButtonModule, IrisIconButtonModule],
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaginationComponent implements OnChanges, OnDestroy {
  @Input() totalItems: number = PAGINATION_DEFAULTS.totalItems;
  @Input() itemsPerPage: number = PAGINATION_DEFAULTS.itemsPerPage;
  @Input() currentPage: number = PAGINATION_DEFAULTS.currentPage;

  @Output() pageChanged = new EventEmitter<number>();

  readonly irisButtonTypes = IrisButtonTypes;
  readonly irisIconButtonTypes = IrisIconButtonTypes;

  readonly totalPages$ = new BehaviorSubject<number>(PAGINATION_DEFAULTS.totalItems);
  readonly currentPage$ = new BehaviorSubject<number>(PAGINATION_DEFAULTS.currentPage);
  readonly visiblePages$ = new BehaviorSubject<number[]>([]);
  private readonly destroy$ = new Subject<void>();
  private readonly totalVisiblePages: number = PAGINATION_DEFAULTS.visiblePages;

  viewModel$: Observable<IPaginationViewModel> = combineLatest([
    this.totalPages$,
    this.currentPage$,
    this.visiblePages$
  ]).pipe(
    takeUntil(this.destroy$),
    map(([totalPages, currentPage, visiblePages]) => {
      const firstVisiblePage = visiblePages[0];
      const lastVisiblePage = visiblePages[visiblePages.length - 1];

      const shouldShowFirstPage = totalPages > this.totalVisiblePages &&
        firstVisiblePage > PAGINATION_DEFAULTS.minPageNumber;
      const shouldShowLastPage = totalPages > this.totalVisiblePages && lastVisiblePage < totalPages;

      const shouldShowStartEllipsis = shouldShowFirstPage &&
        firstVisiblePage > PAGINATION_DEFAULTS.minPageNumber + 1;
      const shouldShowEndEllipsis = shouldShowLastPage && lastVisiblePage < totalPages - 1;

      return {
        shouldShowPagination: totalPages > PAGINATION_DEFAULTS.minPageNumber,
        isFirstPage: currentPage === PAGINATION_DEFAULTS.minPageNumber,
        isLastPage: currentPage === totalPages,
        showStartEllipsis: shouldShowStartEllipsis,
        showEndEllipsis: shouldShowEndEllipsis,
        shouldShowFirstPage,
        shouldShowLastPage,
        visiblePagesInfo: visiblePages.map((page) => this.createPageInfo(page, currentPage)),
        firstPageInfo: this.createPageInfo(PAGINATION_DEFAULTS.minPageNumber, currentPage),
        totalPagesInfo: this.createPageInfo(totalPages, currentPage)
      };
    })
  );

  private createPageInfo(page: number, currentPage: number): IPageInfo {
    const isCurrent = page === currentPage;
    return {
      page,
      buttonType: isCurrent ? IrisButtonTypes.PRIMARY : IrisButtonTypes.TERTIARY,
      ariaCurrent: isCurrent ? 'page' : null,
      ariaLabel: isCurrent ? `Current page, page ${page}` : `Go to page ${page}`
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['totalItems']) {
      this.totalItems = Math.max(0, this.totalItems);
    }
    if (changes['itemsPerPage']) {
      this.itemsPerPage = Math.max(PAGINATION_DEFAULTS.minItemsPerPage, this.itemsPerPage);
    }
    if (changes['currentPage']) {
      this.currentPage = Math.max(PAGINATION_DEFAULTS.minPageNumber, this.currentPage);
    }

    if (changes['totalItems'] || changes['itemsPerPage'] || changes['currentPage']) {
      this.calculatePagination();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.totalPages$.complete();
    this.currentPage$.complete();
    this.visiblePages$.complete();
  }

  trackByPageInfo(index: number, pageInfo: IPageInfo): string {
    return `${pageInfo.page}-${pageInfo.buttonType}`;
  }

  private calculatePagination(): void {
    if (this.totalItems <= PAGINATION_DEFAULTS.totalItems) {
      this.totalPages$.next(PAGINATION_DEFAULTS.totalItems);
      this.visiblePages$.next([]);
      return;
    }

    const totalPages = Math.max(
      PAGINATION_DEFAULTS.minPageNumber,
      Math.ceil(this.totalItems / this.itemsPerPage)
    );

    const currentPage = Math.max(
      PAGINATION_DEFAULTS.minPageNumber,
      Math.min(this.currentPage, totalPages)
    );

    this.totalPages$.next(totalPages);
    this.currentPage$.next(currentPage);
    this.visiblePages$.next(this.calculateVisiblePages(totalPages, currentPage));
  }

  private calculateVisiblePages(totalPages: number, currentPage: number): number[] {
    const {
      minPageNumber,
      visiblePages,
      startEdgeThreshold,
      endEdgeThreshold
    } = PAGINATION_DEFAULTS;

    if (totalPages <= visiblePages) {
      return Array.from({ length: totalPages }, (unusedValue, i) => i + minPageNumber);
    }

    if (currentPage <= startEdgeThreshold) {
      return [1, 2, 3, 4, 5];
    }

    if (currentPage >= totalPages - endEdgeThreshold) {
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
  }

  goToPage(page: number): void {
    const { minPageNumber } = PAGINATION_DEFAULTS;
    const currentPage = this.currentPage$.value;
    const totalPages = this.totalPages$.value;

    if (page === currentPage || page < minPageNumber || page > totalPages || !Number.isInteger(page)) {
      return;
    }

    this.currentPage = page;
    this.calculatePagination();
    this.pageChanged.emit(page);
  }

  previousPage(): void {
    this.goToPage(this.currentPage$.value - 1);
  }

  nextPage(): void {
    this.goToPage(this.currentPage$.value + 1);
  }
}
